import { BestPractices } from '../BestPractices/BestPractices'
import { Box, Dialog, IconButton, Stack, Typography } from '@dtx-company/design-system/src'
import { Flowcode } from '@dtx-company/flowcode-generator-browser'
import { InfoOutlined } from '@mui/icons-material'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { selectDisplayScanDestination } from '@app/code/src/machines/locg/locgSelectors'
import { useFlowcodeOptions } from '../hooks/useFlowcodeOptions'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useSelector } from '@xstate/react'
import React, { FC, useId, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'

export const CodePreview: FC = () => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const options = useFlowcodeOptions()
  const [isOpen, setIsOpen] = useState(false)
  const displayScanDestination = useSelector(locgService, selectDisplayScanDestination)

  const calloutTitleId = useId()
  const calloutDescriptionId = useId()

  return (
    <Stack
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: 'relative',
        flex: {
          md: 1
        },
        overflowY: {
          md: 'auto'
        },
        py: {
          xs: 6,
          md: 10
        },
        height: {
          xs: '30vh',
          md: '100%'
        },
        backgroundColor: 'surface.2'
      }}
      gap={4}
    >
      <Stack gap={8} sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Typography variant="headlineXSmall" align="center">
          {t('LoggedOutGenerator.CodePreview.heading')}
        </Typography>
        <Stack gap={4}>
          <Typography variant="labelSmall" align="center" color="onSurface.secondary">
            {t('LoggedOutGenerator.CodePreview.description')}
          </Typography>
          <Typography variant="bodyMedium" color="onSurface.primary" align="center">
            {displayScanDestination}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={theme => ({
          height: '100%',
          maxHeight: theme.spacing(85)
        })}
      >
        <Flowcode options={options} />
      </Box>

      {/* Desktop callout */}
      <Box
        sx={theme => ({
          display: { xs: 'none', md: 'block' },
          maxWidth: {
            md: '90%',
            lg: theme.spacing(120)
          }
        })}
      >
        <BestPractices />
      </Box>

      {/* Mobile callout */}
      <Box
        sx={theme => ({
          position: 'absolute',
          top: theme.spacing(3),
          right: theme.spacing(4),
          display: { xs: 'block', md: 'none' }
        })}
      >
        <IconButton
          color="secondary"
          icon={InfoOutlined}
          onClick={() => setIsOpen(true)}
          aria-label={t('LoggedOutGenerator.CodePreview.calloutIconLabel')}
          data-heap-id="LoggedOutGenerator_CodePreview_BestPracticesButton"
        />
        <Dialog
          hideBackdrop
          open={isOpen}
          onClose={() => {
            setIsOpen(false)
            fireAnalyticsEvent('LoggedOutGenerator_CodePreview_BestPracticesDialogClosed')
          }}
          aria-labelledby={calloutTitleId}
          aria-describedby={calloutDescriptionId}
        >
          <BestPractices titleId={calloutTitleId} descriptionId={calloutDescriptionId} />
        </Dialog>
      </Box>
    </Stack>
  )
}
